import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { errorMessage, API_BASE } from "../lib/api";
import LinkedEntityFilters from "./LinkedEntityFilters";
import RosterHealthSection from "./RosterHealthSection";
import { GhostButton, inputClass } from "./Primitives";
import {
  RefreshCw,
  Percent,
  Clock,
  DollarSign,
  AlertTriangle,
  LogOut,
  AlertOctagon,
  Wrench,
  Route as RouteIcon,
  ShieldCheck,
  UserX,
  Download,
  FileText,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function currentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return { start: isoDate(start), end: isoDate(now) };
}

function money(n) {
  if (n == null || Number.isNaN(n)) return "$0";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function pctTone(v) {
  if (v == null) return "default";
  if (v >= 95) return "green";
  if (v >= 80) return "amber";
  return "red";
}

function tardyTone(v) {
  if (v == null) return "default";
  if (v <= 5) return "green";
  if (v <= 15) return "amber";
  return "red";
}

function Tile({ icon: Icon, label, value, sub, tone = "default", testid, to }) {
  const toneClass = {
    green: "text-[#34C759]",
    amber: "text-[#FF9F0A]",
    red: "text-[#FF3B30]",
    default: "text-white",
  }[tone] || "text-white";
  const body = (
    <>
      <div className="flex items-center justify-between">
        <div className="label-mute">{label}</div>
        <div className="flex items-center gap-1.5">
          {to && (
            <ArrowUpRight
              className="size-3.5 text-zinc-500 group-hover:text-white transition-colors"
              aria-hidden
            />
          )}
          {Icon && <Icon className={`size-4 ${toneClass}`} />}
        </div>
      </div>
      <div className={`font-display text-3xl font-black tracking-tight tabular-nums ${toneClass}`}>
        {value}
      </div>
      {sub && <div className="text-[10px] uppercase tracking-widest text-zinc-500">{sub}</div>}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className="group border border-[#27272A] bg-[#121212] hover:bg-[#1c1c1e] hover:border-zinc-500 transition-colors p-4 flex flex-col gap-2"
        data-testid={testid}
      >
        {body}
      </Link>
    );
  }
  return (
    <div
      className="border border-[#27272A] bg-[#121212] p-4 flex flex-col gap-2"
      data-testid={testid}
    >
      {body}
    </div>
  );
}

export default function OperationalMetricsTab({ siteId: siteIdProp = null, hideSiteFilter = false } = {}) {
  const [{ start, end }, setRange] = useState(currentMonthRange());
  const [entity, setEntity] = useState({ client_names: [], site_ids: [], guard_ids: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // iter306 — When the parent (Ops Dashboard) drives a site selection,
  // it wins over the tab's internal filter. Keeps state in one place so
  // the two selectors never disagree.
  const effectiveSiteIds = siteIdProp ? [siteIdProp] : (entity.site_ids || []);

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start, end };
      if (effectiveSiteIds.length) params.site_id = effectiveSiteIds.join(",");
      if (entity.client_names?.length) params.client_name = entity.client_names.join(",");
      const res = await api.get("/metrics/operational", { params });
      setData(res.data);
    } catch (e) {
      toast.error(errorMessage(e, "Failed to load metrics"));
      setData(null);
    }
    setLoading(false);
  }, [start, end, entity, siteIdProp, effectiveSiteIds]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const exportTo = (fmt) => {
    const params = new URLSearchParams({ start, end, format: fmt });
    if (effectiveSiteIds.length) params.set("site_id", effectiveSiteIds.join(","));
    if (entity.client_names?.length) params.set("client_name", entity.client_names.join(","));
    window.open(`${API_BASE}/metrics/operational?${params.toString()}`, "_blank", "noopener");
  };

  const filtersShow = useMemo(
    () => ({ client: true, site: !hideSiteFilter, guard: false }),
    [hideSiteFilter]
  );

  return (
    <div className="space-y-4" data-testid="metrics-tab">
      <div className="text-xs text-zinc-500" data-testid="metrics-description">
        Operational & financial KPIs across the selected date range. Coverage compares adjusted worked
        hours vs. scheduled hours. Unrealized revenue defaults to <span className="font-mono text-zinc-300">${data?.default_bill_rate ?? 28}/hr</span> and honours
        each site&apos;s <span className="font-mono text-zinc-300">bill_rate_per_hour</span> when set.
      </div>

      {/* Filters */}
      <div className="space-y-3" data-testid="metrics-filters">
        <LinkedEntityFilters
          testIdPrefix="metrics-filter"
          value={entity}
          onChange={setEntity}
          show={filtersShow}
        />
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="label-mute mb-1">Start</div>
            <input
              data-testid="metrics-start"
              type="date"
              className={inputClass}
              value={start}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
            />
          </div>
          <div>
            <div className="label-mute mb-1">End</div>
            <input
              data-testid="metrics-end"
              type="date"
              className={inputClass}
              value={end}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
            />
          </div>
          <div className="ml-auto inline-flex items-center gap-3">
            <GhostButton data-testid="metrics-refresh" onClick={fetchMetrics}>
              <RefreshCw className="size-3.5" /> Refresh
            </GhostButton>
            <GhostButton data-testid="metrics-export-csv" onClick={() => exportTo("csv")}>
              <Download className="size-3.5" /> CSV
            </GhostButton>
            <GhostButton data-testid="metrics-export-pdf" onClick={() => exportTo("pdf")}>
              <FileText className="size-3.5" /> PDF
            </GhostButton>
          </div>
        </div>
      </div>

      {loading ? (
        <div
          className="text-center py-16 text-zinc-500 scanlines border border-[#27272A] bg-[#121212]"
          data-testid="metrics-loading"
        >
          Loading metrics…
        </div>
      ) : !data ? null : (
        <>
          {/* KPI grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            <Tile
              testid="metric-coverage"
              icon={Percent}
              label="Coverage Fulfillment"
              value={`${data.coverage_fulfillment_pct}%`}
              sub={`${data.total_adjusted_hours}h / ${data.total_scheduled_hours}h sched`}
              tone={pctTone(data.coverage_fulfillment_pct)}
            />
            <Tile
              testid="metric-unrealized-hours"
              icon={Clock}
              label="Unrealized Hours"
              value={`${data.unrealized_hours}h`}
              sub={`${data.shifts_considered} shifts in range`}
              tone={data.unrealized_hours > 0 ? "amber" : "green"}
            />
            <Tile
              testid="metric-unrealized-revenue"
              icon={DollarSign}
              label="Unrealized Revenue"
              value={money(data.unrealized_revenue)}
              sub={`Default rate $${data.default_bill_rate}/hr`}
              tone={data.unrealized_revenue > 0 ? "red" : "green"}
            />
            <Tile
              testid="metric-tardy-pct"
              icon={AlertTriangle}
              label="Tardy %"
              value={`${data.tardy_pct}%`}
              sub={`${data.tardy_shifts} tardy shifts`}
              tone={tardyTone(data.tardy_pct)}
            />
            <Tile
              testid="metric-early-out-pct"
              icon={LogOut}
              label="Early Clock-outs %"
              value={`${data.early_clock_out_pct}%`}
              sub={`${data.early_clock_out_shifts} shifts`}
              tone={tardyTone(data.early_clock_out_pct)}
            />
            <Tile
              testid="metric-incidents"
              icon={AlertOctagon}
              label="Incidents"
              value={data.incidents_count}
              sub="All incidents in range"
            />
            <Tile
              testid="metric-maintenance"
              icon={Wrench}
              label="Maintenance Requests"
              value={data.maintenance_requests_count}
              sub="All requests in range"
            />
            <Tile
              testid="metric-tours-scanned"
              icon={RouteIcon}
              label="Tours Scanned"
              value={data.tours_scanned_count}
              sub="Total checkpoint scans"
            />
            <Tile
              testid="metric-supervisor-visits"
              icon={ShieldCheck}
              label="Supervisor Visits"
              value={data.supervisor_visits_count}
              sub="Field audits logged"
            />
            <Tile
              testid="metric-no-shows"
              icon={UserX}
              label="No-Show Officers"
              value={data.no_show_count}
              sub="Click for the full report"
              tone={data.no_show_count > 0 ? "red" : "green"}
              to={`/reports/no-shows?start=${start}&end=${end}`}
            />
          </div>

          {/* Roster Health — spot guards approaching weekly overtime before
              payroll gets ugly. Sun→Sat US-payroll week, agency-configurable
              threshold (default 40h). */}
          <RosterHealthSection />
        </>
      )}
    </div>
  );
}
